import { useEffect, useMemo } from "react"

import { EllipsisOutlined, StarOutlined } from "@ant-design/icons"
import { Col, Divider, Dropdown, Flex, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import Button from "../../../Components/Button"
import { PaymentDefaultTag } from "../../../Components/Payments/PaymentDefaultTag"
import ShopifyDefaultRow from "../../../Components/Payments/ShopifyDefaultRow"
import withPermission from "../../../hoc/withPermission"
import Private from "../../../Components/Private"
import { useExternalDemoMode } from "../../../hooks/useExternalDemoMode"
import Icon from "../../../Icon"
import Images from "../../../Images"
import { CARD_BRANDS } from "../../../Utils/cardBrands"
import { highAccessRoles } from "../../../Utils/permission"
import AppUtils from "../../../Utils/utils"
import UserCreators from "../../User/reducer"
import { usePageQuery } from "../usePageQuery"

const { Text } = Typography

const PermissionButton = withPermission(Button, highAccessRoles)

const CardDropdown = ({ items, disabled, cardId, actionClick }) => (
  <Dropdown
    overlayClassName="custom-menu"
    menu={{
      items,
      onClick: ({ key }) => actionClick(key, cardId)
    }}
    trigger={["click"]}
    disabled={disabled}
    placement="bottomLeft"
    overlayStyle={{ minWidth: 200 }}
  >
    <EllipsisOutlined style={{ fontSize: 20, cursor: disabled ? "not-allowed" : "pointer" }} />
  </Dropdown>
)

const PermissionCardDropdown = withPermission(CardDropdown, highAccessRoles)

const PaymentMethods = ({
  theme,
  businessAccountId,
  usersCards,
  paymentMethodsRef,
  isAdmin,
  ba_role,
  userInfo,
  balance,
  isShopifyOnlyUser,
  isMobile,
  ...props
}) => {
  const { search } = useLocation()
  const { user_id } = usePageQuery(search)
  const navigate = useNavigate()
  const externalDemoMode = useExternalDemoMode()

  const { t } = useTranslation()

  const businessAccountRole = ba_role;
  const hasBalanceManagementAccess = highAccessRoles.includes(Number(businessAccountRole));
  const has_bussiness_account = balance?.enable_to_topup;
  const isDemoModeBlocked =
    !userInfo?.new_onboarding_finished && userInfo?.next_step < 8;
  const isAddPaymentMethodDisabled =
    !hasBalanceManagementAccess &&
    (!balance?.["activated"] ||
      !has_bussiness_account ||
      externalDemoMode ||
      isDemoModeBlocked);

  const items = useMemo(
    () => [
      {
        key: 0,
        title: "Make default",
        label: (
          <Flex>
            <StarOutlined />
            {isShopifyOnlyUser ? "Make Backup" : t("Make default")}
          </Flex>
        )
      },
      {
        key: 1,
        title: "Remove",
        label: (
          <Flex>
            <Icon type={"delete"} />
            {t("Remove")}
          </Flex>
        )
      }
    ],
    [isShopifyOnlyUser]
  )

  // useEffect(() => {
  //   if (businessAccountId || user_id) {
  //     props.getUsersCards({
  //       //...(user_id ? { user_id } : { business_account_id: businessAccountId })
  //        user_id: user_id || userInfo?.id
  //     });
  //   }
  // }, [businessAccountId, user_id, userInfo?.id]);

  useEffect(() => {
    if (user_id || userInfo?.id) {
      props.getUsersCards({
        user_id: user_id || userInfo?.id
      })
    }
  }, [user_id, userInfo?.id])

  const actionClick = (key, id) => {
    if (+key) {
      props.deleteCard({ card_id: id, ...(isAdmin && { user_id }) })
    } else {
      props.makeDefaultCard({
        card_id: id,
        data: { active: true },
        ...(isAdmin && { user_id })
      })
    }
  }

  const handleAddPaymentMethod = () => {
    if (!balance?.["activated"] && !hasBalanceManagementAccess) return
    navigate("/add-card")
  }

  if (isMobile) {
    return (
      <Flex vertical gap={16}>
        <Flex vertical gap={8}>
          <Text strong style={{ fontSize: 16 }}>
            {t("Payment methods")}
          </Text>
          <Text type="secondary">
            {t(
              "Add prepaid funds to pay for alerts in advance. We’ll deduct from prepaid funds first before charging any payment methods with automatic billing turned on."
            )}
          </Text>
        </Flex>

        <Flex vertical gap={12}>
          <Text type="secondary" strong style={{ fontSize: 14 }}>
            {t("Payment method")}
          </Text>
          <Flex vertical gap={4} ref={paymentMethodsRef}>
            {usersCards &&
              usersCards.map((card, index) => (
                <div key={card.id}>
                  {index === 0 && <Divider style={{ margin: "0 0 4px" }} />}
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      height: 36,
                      padding: "4px 0"
                    }}
                  >
                    <Icon type={card.brand} />
                    <Text style={{ fontSize: 14, fontWeight: 500 }}>
                      {AppUtils.toUpperLatter(card.brand)}
                    </Text>
                    {card.is_default && <PaymentDefaultTag />}
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 500 }}>
                      {"•••• "}
                      {card.last4}
                    </Text>
                    <Text style={{ fontSize: 12 }}>
                      <Private>
                        {t("Expires on")}{" "}
                        {card.exp_month > 9 ? card.exp_month : "0" + card.exp_month}/
                        {String(card.exp_year).slice(2, 4)}
                      </Private>
                    </Text>
                    <div style={{ position: "absolute", right: 0 }}>
                      {!card.is_default && !isAdmin ? (
                        <PermissionCardDropdown
                          items={items}
                          disabled={card.is_default}
                          cardId={card.id}
                          actionClick={actionClick}
                          role={businessAccountRole}
                        />
                      ) : isAdmin ? (
                        <CardDropdown
                          items={items.filter((item) => item.key !== 0)}
                          disabled={balance?.amount < 0 || card?.can_remove === false}
                          cardId={card.id}
                          actionClick={actionClick}
                        />
                      ) : (
                        <div style={{ width: 20 }} />
                      )}
                    </div>
                  </div>
                  <Divider style={{ margin: "4px 0 0" }} />
                </div>
              ))}
            {isAdmin ? null : (
              <div style={{ padding: "8px 0" }}>
                <PermissionButton
                  onClick={handleAddPaymentMethod}
                  type="link"
                  disabled={isAddPaymentMethodDisabled}
                  style={{ padding: 0, fontWeight: 500, height: 22 }}
                  role={businessAccountRole}
                >
                  {"+ "}
                  {t("Add payment method")}
                </PermissionButton>
              </div>
            )}
          </Flex>
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex justify="space-between">
      <Col xs={24} sm={24} md={12} lg={12}>
        <Space size={8} direction="vertical">
          <Text strong>{isShopifyOnlyUser ? "How you will pay" : t("Payment methods")}</Text>
          <Text type="secondary">
            {t(
              "Add prepaid funds to pay for alerts in advance. We’ll deduct from prepaid funds first before charging any payment methods with automatic billing turned on."
            )}
          </Text>
        </Space>
      </Col>

      <Col ref={paymentMethodsRef} flex="1 1 420" className="card-width">
        <Text type="secondary" strong>
          {isShopifyOnlyUser ? "How you will pay" : t("Payment method")}
        </Text>
        {usersCards && (
          <Col style={{ marginTop: 12, paddingLeft: 0 }}>
            {isShopifyOnlyUser && <ShopifyDefaultRow />}
            {usersCards.map((card, index) => (
              <PaymentMethodRow
                key={card.id}
                card={card}
                index={index}
                theme={theme}
                isShopifyOnlyUser={isShopifyOnlyUser}
                isAdmin={isAdmin}
                businessAccountRole={businessAccountRole}
                actionItems={items}
                balance={balance}
                actionClick={actionClick}
                t={t}
              />
            ))}
          </Col>
        )}
        {isAdmin ? null : (
          <PermissionButton
            onClick={handleAddPaymentMethod}
            type="link"
            disabled={isAddPaymentMethodDisabled}
            style={{
              marginTop: 12,
              padding: 0,
              fontWeight: 500,
              height: 22
            }}
            role={businessAccountRole}
          >
            + {t("Add payment method")}
          </PermissionButton>
        )}
      </Col>
    </Flex>
  )
}

const PaymentMethodRow = ({
  card,
  index,
  theme,
  isShopifyOnlyUser,
  isAdmin,
  businessAccountRole,
  actionItems,
  balance,
  actionClick,
  t
}) => {
  const backupTagStyles = {
    color: theme === "dark" ? "#7A7A83" : "#7A7A83",
    background: theme === "dark" ? "#1D1D1D" : "#F4F4F5"
  }

  return (
    <div>
      {index === 0 && !isShopifyOnlyUser && <Divider style={{ margin: "0px 0px" }} />}
      <Flex justify="space-between" align="center" style={{ marginTop: 8 }}>
        <Flex flex={1} align="center" gap={8} justify="space-between">
          <Flex gap={8}>
            {card.brand === "visa" ? (
              <Icon type={card.brand} />
            ) : CARD_BRANDS.includes(card.brand) ? (
              <img src={Images[`${card.brand}${theme === "dark" ? "_dark" : ""}`]} />
            ) : null}
            <Text className="text-min">{AppUtils.toUpperLatter(card.brand)}</Text>
            {card.is_default && (
              <PaymentDefaultTag
                style={{
                  ...(isShopifyOnlyUser
                    ? {
                        color: theme === "dark" ? "#7A7A83" : "#7A7A83",
                        background: theme === "dark" ? "#1D1D1D" : "#F4F4F5"
                      }
                    : {})
                }}
              >
                {isShopifyOnlyUser ? "Backup" : t("Default")}
              </PaymentDefaultTag>
            )}
          </Flex>
          <Text type="secondary">•••• {card.last4}</Text>
        </Flex>

        <Flex align="center" gap={12}>
          <Flex align="center" gap={6}>
            <Divider type="vertical" />
            <Text>
              <Private>
                {t("Expires on")} {card.exp_month > 9 ? card.exp_month : `0${card.exp_month}`}/
                {String(card.exp_year).slice(2, 4)}
              </Private>
            </Text>
            <Divider type="vertical" />
          </Flex>

          {!card.is_default ? (
            <PermissionCardDropdown
              items={actionItems}
              disabled={card.is_default}
              cardId={card.id}
              actionClick={actionClick}
              role={businessAccountRole}
            />
          ) : (
            <div style={{ width: 20 }} />
          )}
        </Flex>
      </Flex>
      <Divider style={{ margin: "12px 0px" }} />
    </div>
  )
}

const mapStateToProps = (state) => ({
  theme: state.nav.theme,
  businessAccountId: state.user.businessAccount?.id,
  usersCards: state.user.usersCards,
  isAdmin: state.auth.isAdmin,
  ba_role: state.auth.userInfo?.ba_role?.id,
  userInfo: state.auth.userInfo,
  balance: state.balance.balance
})

const mapDispatchToProps = (dispatch) => ({
  getUsersCards: (params) => dispatch(UserCreators.getUsersCardsRequest(params)),
  deleteCard: (params) => dispatch(UserCreators.deleteUserCardRequest(params)),
  makeDefaultCard: ({ card_id, data, user_id }) =>
    dispatch(UserCreators.updateDefaultCardRequest({ card_id, data, user_id }))
})

export default connect(mapStateToProps, mapDispatchToProps)(PaymentMethods)
